import { Component, Event, EventEmitter, h, Listen, Prop, State } from '@stencil/core';
import { MapNavigationEvent } from '@hpfc/fc-frontend-core';
import { MAPBOX_CARD_NARROW_HEIGHT, MAPBOX_CARD_WIDTH } from '../mapbox/MapboxConstants';

export type MapCardWidth = 'wide' | 'narrow';

/**
 * Card container for the vertical content designed to be displayed over a map.
 */
@Component({
  tag: 'fccd-map-card',
  styleUrl: 'fccd-map-card.css',
  shadow: true,
})
export class MapCard {
  private topEl: HTMLElement;

  /**
   * Optimize the card for different screen widths.
   */
  @Prop()
  screenWidth: MapCardWidth = 'wide';

  @State()
  topHeight: number;

  /**
   * Map card fires this event when the close button is clicked, or when the card contains focus.
   */
  @Event() mapNavigationEvent: EventEmitter<MapNavigationEvent>;

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      this.onClose();
    }
  }

  componentDidLoad() {
    this.topHeight = this.topEl.clientHeight;
  }

  onClose() {
    const data = new MapNavigationEvent('close', undefined, undefined);
    this.mapNavigationEvent.emit(data);
  }

  getTopHeight() {
    if (!this.topHeight) {
      return '0px';
    }
    return `${this.topHeight}px`;
  }

  getDimensions() {
    if (this.screenWidth === 'narrow') {
      return { width: '100%', height: `${MAPBOX_CARD_NARROW_HEIGHT}px` };
    }
    return { maxWidth: `${MAPBOX_CARD_WIDTH}px` };
  }

  render() {
    return (
      <div class="base-card" style={{ scrollPaddingTop: this.getTopHeight(), ...this.getDimensions() }}>
        <div class="top" ref={el => (this.topEl = el)}>
          <div class="close-button">
            <hp5-icon-button name="system-close" size-selection="sm" icon-size-selection="sm" type="floating" ally-label="Close" onClick={this.onClose.bind(this)} />
          </div>
          <slot name="top" />
        </div>
        <slot name="image" />
        <div class="container">
          <slot name="location" />
          <slot name="callToAction" />
        </div>
      </div>
    );
  }
}
